import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API, getPhotoUrl } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Menu, X } from "lucide-react";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const BrowseProfiles = () => {
  const { t } = useTranslation();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Determine opposite gender - for matrimonial site, men see women and women see men ONLY
  const getOppositeGender = () => {
    if (user?.gender === "Male") return "Female";
    if (user?.gender === "Female") return "Male";
    return "Female"; // Default fallback
  };
  
  const [filters, setFilters] = useState({
    gender: "",  // Will be set properly in useEffect
    min_age: "",
    max_age: "",
    muslim_type: "",
    country: "",
    city: "",
    ethnicity: "",
    marital_status: "",
    education: "",
    has_children: null,
    hijab_preference: "",
    beard_preference: "",
    fashion_style: "",
    has_photos: ""  // "with_photos", "without_photos", or "" for all
  });

  // Initialize filters and search when user is loaded
  useEffect(() => {
    fetchOptions();
  }, []);
  
  // Set correct gender filter when user loads and trigger search
  useEffect(() => {
    if (user) {
      const oppositeGender = getOppositeGender();
      setFilters(prev => ({ ...prev, gender: oppositeGender }));
    }
  }, [user]);
  
  // Search when filters change (including initial gender set)
  useEffect(() => {
    if (filters.gender) {
      searchProfiles();
    }
  }, [filters.gender]);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/config/options`);
      setOptions(response.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const searchProfiles = async () => {
    setLoading(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v !== null)
      );
      
      const response = await axios.post(`${API}/search`, cleanFilters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
    } catch (error) {
      console.error("Error searching profiles:", error);
      toast.error(t('browse.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleRequestPhotoAccess = async (userId, userName) => {
    try {
      await axios.post(`${API}/photos/request-view/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Photo access request sent to ${userName}`);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('browse.failedRequest'));
      }
    }
  };

  const applyFilters = () => {
    searchProfiles();
    setShowFilters(false);
  };

  const clearFilters = () => {
    const oppositeGender = getOppositeGender();
    setFilters({
      gender: oppositeGender,  // Always keep opposite gender - cannot be changed
      min_age: "",
      max_age: "",
      muslim_type: "",
      country: "",
      city: "",
      ethnicity: "",
      marital_status: "",
      education: "",
      has_children: null,
      hijab_preference: "",
      beard_preference: "",
      fashion_style: "",
      has_photos: ""
    });
  };

  const handleSendInterest = async (profileId) => {
    try {
      await axios.post(`${API}/interests/send`, { to_user_id: profileId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Interest sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('browse.errorSendingInterest'));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Check subscription for photo viewing
  const hasActiveSubscription = user?.subscription && user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();

  return (
    <div className="min-h-screen bg-rose-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-rose-100/50" data-testid="browse-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-rose-600 hover:text-rose-800 transition-colors">Dashboard</Link>
              <Link to="/browse" className="text-rose-800 font-medium">Browse</Link>
              <Link to="/chat" className="text-rose-600 hover:text-rose-800 transition-colors">Chat</Link>
              <Link to="/settings" className="text-rose-600 hover:text-rose-800 transition-colors">Settings</Link>
              <button onClick={handleLogout} className="text-rose-600 hover:text-red-600 transition-colors">Logout</button>
            </div>

            <button 
              className="md:hidden p-2 text-rose-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-rose-100 py-4 px-4">
            <div className="flex flex-col gap-4">
              <Link to="/dashboard" className="text-rose-600 py-2">{t('nav.dashboard')}</Link>
              <Link to="/chat" className="text-rose-600 py-2">{t('nav.chat')}</Link>
              <Link to="/settings" className="text-rose-600 py-2">{t('nav.settings')}</Link>
              <div className="py-2">
                <LanguageCurrencySelector />
              </div>
              <button onClick={handleLogout} className="text-red-600 py-2 text-left">{t('nav.logout')}</button>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-44 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-rose-900 mb-2" data-testid="browse-heading">{t('browse.title')}</h1>
            <p className="text-rose-600">{t('browse.subtitle', { count: profiles.length })}</p>
          </div>

          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="border-rose-500 text-rose-600 hover:bg-rose-50 rounded-full px-6"
            data-testid="filter-toggle-btn"
          >
            Filters {showFilters ? "−" : "+"}
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-6 mb-8 animate-fade-in-up" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-rose-700">Age Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder={t('browse.minPlaceholder')}
                    value={filters.min_age}
                    onChange={(e) => handleFilterChange("min_age", e.target.value ? parseInt(e.target.value) : "")}
                    className="bg-rose-50 border-rose-200 h-10"
                    data-testid="filter-min-age"
                  />
                  <Input
                    type="number"
                    placeholder={t('browse.maxPlaceholder')}
                    value={filters.max_age}
                    onChange={(e) => handleFilterChange("max_age", e.target.value ? parseInt(e.target.value) : "")}
                    className="bg-rose-50 border-rose-200 h-10"
                    data-testid="filter-max-age"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Muslim Type</Label>
                <Select value={filters.muslim_type || "all"} onValueChange={(v) => handleFilterChange("muslim_type", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-muslim-type">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {options?.muslim_types?.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Country</Label>
                <Select value={filters.country || "all"} onValueChange={(v) => handleFilterChange("country", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-country">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {options?.countries?.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">City</Label>
                <Input
                  placeholder={t('browse.enterCity')}
                  value={filters.city}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                  className="bg-rose-50 border-rose-200 h-10"
                  data-testid="filter-city"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Ethnicity</Label>
                <Select value={filters.ethnicity || "all"} onValueChange={(v) => handleFilterChange("ethnicity", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-ethnicity">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {options?.ethnicities?.map((eth) => (
                      <SelectItem key={eth} value={eth}>{eth}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Marital Status</Label>
                <Select value={filters.marital_status || "all"} onValueChange={(v) => handleFilterChange("marital_status", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-marital-status">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="never_married">Never Married</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Education</Label>
                <Select value={filters.education || "all"} onValueChange={(v) => handleFilterChange("education", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-education">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {options?.education_levels?.map((edu) => (
                      <SelectItem key={edu} value={edu}>{edu}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Has Children</Label>
                <Select 
                  value={filters.has_children === null ? "all" : filters.has_children ? "yes" : "no"} 
                  onValueChange={(v) => handleFilterChange("has_children", v === "all" ? null : v === "yes")}
                >
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-has-children">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="no">No Children</SelectItem>
                    <SelectItem value="yes">Has Children</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Hijab filter - show when searching for females */}
              {filters.gender === "Female" && (
                <div className="space-y-2">
                  <Label className="text-stone-700">Hijab</Label>
                  <Select value={filters.hijab_preference || "all"} onValueChange={(v) => handleFilterChange("hijab_preference", v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-hijab">
                      <SelectValue placeholder={t('common.any')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="Always wears hijab">Wears Hijab</SelectItem>
                      <SelectItem value="Sometimes wears hijab">Sometimes</SelectItem>
                      <SelectItem value="Does not wear hijab">No Hijab</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Beard filter - show when searching for males */}
              {filters.gender === "Male" && (
                <div className="space-y-2">
                  <Label className="text-stone-700">Beard</Label>
                  <Select value={filters.beard_preference || "all"} onValueChange={(v) => handleFilterChange("beard_preference", v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-beard">
                      <SelectValue placeholder={t('common.any')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="Full beard">Full Beard</SelectItem>
                      <SelectItem value="Stubble">Stubble</SelectItem>
                      <SelectItem value="Clean shaven">Clean Shaven</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Fashion style filter */}
              <div className="space-y-2">
                <Label className="text-stone-700">Fashion Style</Label>
                <Select value={filters.fashion_style || "all"} onValueChange={(v) => handleFilterChange("fashion_style", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-fashion">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="Modest/Traditional">Modest/Traditional</SelectItem>
                    <SelectItem value="Smart Casual">Smart Casual</SelectItem>
                    <SelectItem value="Modern/High Fashion">Modern/High Fashion</SelectItem>
                    <SelectItem value="Mix of styles">Mix of styles</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Photo filter */}
              <div className="space-y-2">
                <Label className="text-stone-700">Profile Photos</Label>
                <Select value={filters.has_photos || "all"} onValueChange={(v) => handleFilterChange("has_photos", v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-rose-50 border-rose-200 h-10" data-testid="filter-photos">
                    <SelectValue placeholder={t('common.any')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Profiles</SelectItem>
                    <SelectItem value="with_photos">With Photos</SelectItem>
                    <SelectItem value="without_photos">Without Photos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-stone-300 text-stone-700 rounded-full"
                data-testid="clear-filters-btn"
              >
                Clear All
              </Button>
              <Button
                onClick={applyFilters}
                className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full"
                data-testid="apply-filters-btn"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Profiles Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-rose-100">
                <div className="h-64 skeleton" />
                <div className="p-6 space-y-3">
                  <div className="h-6 w-32 skeleton rounded" />
                  <div className="h-4 w-24 skeleton rounded" />
                  <div className="h-4 w-full skeleton rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16" data-testid="no-profiles-message">
            <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl text-rose-300 font-serif">?</span>
            </div>
            <h3 className="font-serif text-xl text-rose-900 mb-2">No Profiles Found</h3>
            <p className="text-rose-600">Try adjusting your filters to see more results</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="profiles-grid">
            {profiles.map((profile) => (
              <div 
                key={profile.id} 
                className="profile-card cursor-pointer"
                onClick={() => navigate(`/profile/${profile.id}`)}
                data-testid={`profile-card-${profile.id}`}
              >
                {/* Photo */}
                <div className="relative h-64 bg-rose-200">
                  {profile.photos?.[0] ? (
                    <>
                      <img 
                        src={getPhotoUrl(profile.photos[0])}
                        alt={profile.first_name}
                        className={`w-full h-full object-cover ${profile.photos_blurred ? 'blur-lg' : ''}`}
                      />
                      {profile.photos_blurred && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="bg-white/95 rounded-xl px-5 py-4 text-center shadow-lg">
                            <span className="text-2xl text-rose-500 block mb-2">👁</span>
                            {profile.can_request_photo_access ? (
                              <>
                                <p className="text-sm font-medium text-gray-900 mb-2">Photo access required</p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestPhotoAccess(profile.id, profile.first_name);
                                  }}
                                  className="text-xs bg-rose-500 text-white px-4 py-2 rounded-full hover:bg-rose-600 transition-colors"
                                  data-testid={`request-photo-${profile.id}`}
                                >
                                  Request Access
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-900">Subscribe to view</p>
                                <p className="text-xs text-gray-500 mt-1">Premium members can see photos</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-rose-100">
                      <span className="text-4xl font-serif text-rose-300">{profile.first_name?.[0] || "?"}</span>
                    </div>
                  )}
                  
                  {/* Online Status Indicator */}
                  <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                    profile.is_online 
                      ? 'bg-green-500 text-white' 
                      : 'bg-rose-700/70 text-white'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${profile.is_online ? 'bg-white animate-pulse' : 'bg-rose-400'}`} />
                    {profile.is_online ? 'Online' : 'Offline'}
                  </div>
                </div>

                {/* Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-xl text-rose-900 flex items-center gap-2">
                      {profile.first_name}
                      {profile.is_id_verified && (
                        <span className="text-blue-500" title="ID Verified">✓</span>
                      )}
                      {profile.date_of_birth && (
                        <span className="text-rose-600 font-sans text-base">
                          {new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear()}
                        </span>
                      )}
                    </h3>
                    {profile.muslim_type && (
                      <span className="text-xs bg-rose-100 text-rose-900 px-2 py-1 rounded-full">
                        {profile.muslim_type}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-rose-600">
                    {profile.country && (
                      <div className="flex items-center gap-2">
                        <span className="text-rose-400">→</span>
                        <span>{profile.city ? `${profile.city}, ` : ''}{profile.country}</span>
                      </div>
                    )}
                    {profile.profession && (
                      <div className="flex items-center gap-2">
                        <span className="text-rose-400">→</span>
                        <span>{profile.profession}</span>
                      </div>
                    )}
                    {profile.education && (
                      <div className="flex items-center gap-2">
                        <span className="text-rose-400">→</span>
                        <span>{profile.education}</span>
                      </div>
                    )}
                  </div>

                  {profile.about_me && (
                    <p className="mt-4 text-rose-600 text-sm line-clamp-2">{profile.about_me}</p>
                  )}

                  <div className="mt-4 space-y-2">
                    {/* Request Photo Access Button - only show if photos are blurred due to "on_request" setting */}
                    {profile.can_request_photo_access && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestPhotoAccess(profile.id);
                        }}
                        variant="outline"
                        className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full"
                        data-testid={`request-photo-${profile.id}`}
                      >
                        Request Photo Access
                      </Button>
                    )}
                    
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendInterest(profile.id);
                      }}
                      className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full"
                      data-testid={`send-interest-${profile.id}`}
                    >
                      Send Interest
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default BrowseProfiles;
